import { LegendStates, MouseSelectionStates } from "types";
import { SeriesType } from "types/echarts";
import { removeCount } from "utils";
import { allTrue } from "utils/fp";
import { removeProbitInfoFromtitle } from "utils/removeProbitInfoFromTitle";

import {
  UNDEFINED_DATA_POINT,
  UNDEFINED_HOVER,
  getGroupName,
  getPointCoordinates,
  getSeriesIndexFromSelectedGroups,
  getSeriesName,
  isValidChartLegendStates
} from "./utils";

export function probitHoverLegends({
  params,
  series,
  forecast,
  response
}: Partial<MouseSelectionStates>) {
  if (!series) return UNDEFINED_HOVER;
  let seriesName = getSeriesName({ params, series, forecast });
  let groupName = getGroupName({ params, series });
  if (seriesName.includes("Fit -")) {
    groupName = removeProbitInfoFromtitle(seriesName);
    groupName = removeCount(groupName);
  } else {
    const wellIndex = series[params.seriesIndex]?.data[params.dataIndex * 3 + 2];
    seriesName = response?.uwis[wellIndex] ?? "";
    if (!seriesName) return UNDEFINED_HOVER;
  }
  return {
    hoverLegendItem: seriesName,
    hoverLegendGroup: groupName,
    hoverLegendId: ""
  };
}

export function probitSelectionParams({ states, series }: Partial<LegendStates>) {
  const { hoverLegendGroup, hoverLegendItem, attentionWells, selectedGroups, syncWells } =
    states;

  const isInvalid = isValidChartLegendStates({ states, series });
  if (isInvalid) return UNDEFINED_DATA_POINT;

  const isHoveringOnChart = allTrue(
    hoverLegendItem?.length > 0,
    hoverLegendGroup?.length > 0,
    hoverLegendGroup !== hoverLegendItem
  );

  const isHoveringOnMap = allTrue(
    hoverLegendItem?.length > 0,
    !hoverLegendGroup,
    attentionWells?.length > 0
  );

  const onlySelectedGroup = allTrue(
    !hoverLegendItem,
    !hoverLegendGroup,
    attentionWells?.length === 0,
    selectedGroups?.length > 0
  );

  const isSyncingToWell =
    !hoverLegendItem && syncWells?.isSync && syncWells?.activeWells?.length > 0;

  const selectedIndices = getSeriesIndexFromSelectedGroups({ states, series });

  const conditions = [
    {
      predicate: () => isHoveringOnChart,
      transform: () => {
        const candidate = getPointCoordinates(states, hoverLegendItem);
        return candidate;
      }
    },
    {
      predicate: () => isHoveringOnMap,
      transform: () => {
        const candidate = getPointCoordinates(states, attentionWells[0]);
        return candidate;
      }
    },
    {
      predicate: () => onlySelectedGroup,
      transform: () => ({
        seriesIndex: [...selectedIndices],
        targetIndex: undefined,
        seriesType: SeriesType.Scatter
      })
    },
    {
      predicate: () => isSyncingToWell,
      transform: () => {
        const candidate = getPointCoordinates(states, syncWells.activeWells[0]);
        return candidate;
      }
    }
  ];

  const result = conditions.find((condition) => condition.predicate())?.transform();

  return result ?? UNDEFINED_DATA_POINT;
}
